import React, { useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.jsx'
import './index.css'
import { logger } from './utils/logger'
// Lazy import performanceMonitor to avoid initialization order issues (performanceMonitor -> logger)
// import { performanceMonitor } from './utils/performanceMonitoring'
// Lazy import debugUtils to avoid initialization order issues (debugUtils -> environmentConfig -> logger)
// import { setupDebugUtils } from './utils/debugUtils'
// Lazy import performanceOptimizer to avoid initialization order issues (performanceOptimizer -> contentCache -> storage -> logger)
// import { warmCache, preloadResources } from './utils/performanceOptimizer'
// Lazy import contentCache to avoid initialization order issues (contentCache -> storage -> logger)
// import { contentCache } from './utils/contentCache'
// Lazy import studyGuides to reduce initial bundle size (355 lines)
// import { studyGuides } from './data/studyGuidesData'

const MainApp = App;

import { ALL_SUBJECTS } from './constants';

// Normalize URLs BEFORE React mounts to avoid loops and blank screens
(() => {
  try {
    const basePath = import.meta.env?.VITE_BASE_PATH || '/';
    const segments = (window.location.pathname || '').split('/').filter(Boolean);
    const lastSeg = segments[segments.length - 1];
    const expectedHash = lastSeg ? `#/${lastSeg}` : '';
    if (ALL_SUBJECTS.includes(lastSeg) && window.location.hash !== expectedHash) {
      const pathPrefix = basePath === '/' ? '' : basePath.replace(/\/$/, '');
      const target = `${window.location.origin}${pathPrefix}${expectedHash}`;
      if (window.location.href !== target) {
        window.location.replace(target);
      }
    }
  } catch (e) {
    // no-op: fallback to React boundary
  }
})();

// Simple error boundary for initialization
class InitErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Error will be handled by error boundary
    logger.error('App initialization error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 flex items-center justify-center">
          <div className="text-center text-white p-8">
            <div className="text-2xl mb-4">🎮 Emmy's Learning App</div>
            <div className="text-lg mb-4">Loading the app...</div>
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
            <button 
              onClick={() => {
                // Clear any cached data and reload
                localStorage.clear();
                sessionStorage.clear();
                window.location.reload();
              }} 
              className="bg-white text-purple-600 px-6 py-3 rounded-lg font-bold hover:bg-gray-100"
            >
              Start Fresh
            </button>
            <div className="text-xs mt-4 opacity-50">
              If you see this, try the "Start Fresh" button
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Initialize auth debug helper for browser console
if (typeof window !== 'undefined') {
  import('./utils/authDebugHelper').then(module => {
    window.authDebug = module.default;
    logger.debug('Auth debug helper loaded. Use window.authDebug in console.');
  }).catch(err => {
    logger.warn('Failed to load auth debug helper:', err);
  });
}

// Initialize performance monitoring and debug utilities
if (typeof window !== 'undefined') {
  // Lazy load performanceMonitor to avoid initialization order issues
  import('./utils/performanceMonitoring').then(({ performanceMonitor }) => {
    performanceMonitor.init();
  }).catch(err => {
    logger.warn('Failed to load performance monitoring:', err);
  });
  
  // Lazy load debugUtils to avoid initialization order issues
  import('./utils/debugUtils').then(module => {
    module.setupDebugUtils(); // Initialize authentication debugging commands
  }).catch(err => {
    // Silently fail - debug utils are not critical for app operation
    logger.warn('Failed to load debug utilities:', err);
  });
  
  // Initialize cache and warm it up with critical content
  // Lazy load contentCache to avoid initialization order issues
  (async () => {
    try {
      const { contentCache } = await import('./utils/contentCache');
      await contentCache.init();
      
      // Warm cache with study guides (non-blocking, use idle time)
      // Lazy load performanceOptimizer to avoid initialization order issues
      // Cache warming disabled to reduce initial bundle size
      // Study guides are now loaded dynamically in App.jsx
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => {
          import('./utils/performanceOptimizer').then(module => {
            // Warm cache with dynamically loaded study guides
            import('./data/studyGuidesData').then(dataModule => {
              module.warmCache({ studyGuides: dataModule.studyGuides }).catch(err => {
                logger.debug('Cache warming deferred:', err);
              });
            });
          }).catch(err => {
            logger.debug('Failed to load performanceOptimizer:', err);
          });
        }, { timeout: 2000 });
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => {
          import('./utils/performanceOptimizer').then(module => {
            // Warm cache with dynamically loaded study guides
            import('./data/studyGuidesData').then(dataModule => {
              module.warmCache({ studyGuides: dataModule.studyGuides }).catch(err => {
                logger.debug('Cache warming deferred:', err);
              });
            });
          }).catch(err => {
            logger.debug('Failed to load performanceOptimizer:', err);
          });
        }, 2000);
      }
    } catch (error) {
      logger.debug('Cache initialization deferred:', error);
    }
  })();
  
  // Global error handlers for unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    // Filter out Chrome extension errors (not our code)
    const errorMessage = event.reason?.message || String(event.reason || '');
    const errorStr = errorMessage.toLowerCase();
    
    // Suppress common browser extension errors
    if (errorStr.includes('message channel closed') || 
        errorStr.includes('listener indicated an asynchronous response') ||
        errorStr.includes('metamask') ||
        errorStr.includes('wallet') ||
        errorStr.includes('extension not found') ||
        errorStr.includes('failed to connect to metamask') ||
        errorStr.includes('web-client-content-script') ||
        errorStr.includes('mutationobserver') ||
        errorStr.includes('parameter 1 is not of type') ||
        errorStr.includes('err_blocked_by_client')) {
      // Likely Chrome extension error or benign blocked request, suppress from console
      event.preventDefault();
      return;
    }
    
    // Log other unhandled rejections for debugging
    logger.error('Unhandled promise rejection:', event.reason);
    // Don't prevent default for our own errors - let them be logged
  });
  
  // Suppress Chrome extension message errors (they're not from our code)
  const originalError = console.error;
  console.error = (...args) => {
    const errorStr = args.join(' ').toLowerCase();
    // Filter out Chrome extension async message errors, wallet extension errors, and benign network errors
    if (errorStr.includes('message channel closed') || 
        errorStr.includes('listener indicated an asynchronous response') ||
        errorStr.includes('a listener indicated an asynchronous response') ||
        errorStr.includes('metamask') ||
        errorStr.includes('failed to connect to metamask') ||
        errorStr.includes('extension not found') ||
        errorStr.includes('web-client-content-script') ||
        errorStr.includes('mutationobserver') ||
        errorStr.includes('parameter 1 is not of type') ||
        errorStr.includes('err_blocked_by_client') ||
        errorStr.includes('generate_204')) {
      // Suppress Chrome extension errors and benign blocked requests
      return;
    }
    originalError.apply(console, args);
  };
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <InitErrorBoundary>
    <MainApp />
  </InitErrorBoundary>
)
